import { cn } from "@/lib/utils";
import { Badge } from "@/components/badge";
import { CopyFormattedButton } from "@/components/copy-formatted-button";
import type { RedFlag } from "../../../../shared/analysis-types";
import { formatRedFlagCopy } from "@/lib/analysis-copy-format";

const severityConfig = {
  critical: {
    edge: "bg-destructive",
    bg: "bg-danger-soft/40",
    border: "border-error-light",
    label: "CRITICAL",
    labelColor: "text-destructive",
  },
  advisory: {
    edge: "bg-warning",
    bg: "bg-warning-soft/40",
    border: "border-warning-soft",
    label: "ADVISORY",
    labelColor: "text-warning",
  },
} as const;

interface RedFlagCardProps {
  flag: RedFlag;
  className?: string;
}

export function RedFlagCard({ flag, className }: RedFlagCardProps) {
  const severity = severityConfig[flag.severity];

  return (
    <div
      className={cn(
        "group flex overflow-clip rounded-xl border",
        severity.border,
        severity.bg,
        className
      )}
    >
      <div className={cn("w-1 shrink-0", severity.edge)} />
      <div className="flex min-w-0 flex-1 flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="category">
              {flag.category.charAt(0).toUpperCase() + flag.category.slice(1)}
            </Badge>
            <span
              className={cn(
                "text-xs font-bold uppercase tracking-wider",
                severity.labelColor
              )}
            >
              {severity.label}
            </span>
          </div>
          <CopyFormattedButton
            format={formatRedFlagCopy(flag)}
            className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
          >
            Copy item
          </CopyFormattedButton>
        </div>
        <p className="text-base font-semibold text-foreground">{flag.lede}</p>
        <p className="text-sm leading-relaxed text-foreground">{flag.body}</p>
      </div>
    </div>
  );
}
